import React, { useState, useEffect, useRef } from 'react';
import {
  Building2, Loader2, KeyRound, ShieldCheck, ShieldAlert,
  Upload, CloudDownload, RefreshCw, CheckCircle2, AlertCircle
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarCnpj } from '../utils/formatters';
import { useEmpresa } from '../contexts/EmpresaContext';

export default function MinhaEmpresaPage() {
  // Ticket 907: antes pegava sempre empresas[0] (primeira em ordem alfabetica),
  // ignorando qual empresa o usuario tem selecionada no seletor da sidebar.
  const { empresaAtual, carregando: carregandoEmpresa } = useEmpresa() || {};
  const [empresa,          setEmpresa]          = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [certStatus,       setCertStatus]       = useState(null);
  const [nfedistStatus,    setNfedistStatus]    = useState(null);
  const [consultando,      setConsultando]      = useState(false);

  // Certificado
  const [certModal,    setCertModal]    = useState(false);
  const [certForm,     setCertForm]     = useState({ senha: '', validade: '' });
  const [certArquivo,  setCertArquivo]  = useState(null);
  const [certSalvando, setCertSalvando] = useState(false);
  const certInputRef = useRef();

  useEffect(() => {
    if (carregandoEmpresa) return;
    carregar(empresaAtual);
  }, [empresaAtual, carregandoEmpresa]);

  async function carregar(emp) {
    setLoading(true);
    setCertStatus(null);
    setNfedistStatus(null);
    if (!emp) { setEmpresa(null); setLoading(false); return; }
    try {
      setEmpresa(emp);
      const [certRes, nfeRes] = await Promise.allSettled([
        api.get(`/certificados/${emp.id}`),
        api.get(`/nfedist/${emp.id}/status`)
      ]);
      if (certRes.status === 'fulfilled') setCertStatus(certRes.value.data);
      if (nfeRes.status  === 'fulfilled') setNfedistStatus(nfeRes.value.data);
    } catch {
      toast.error('Erro ao carregar dados da empresa.');
    } finally {
      setLoading(false);
    }
  }

  async function consultarSEFAZ() {
    if (!empresa) return;
    setConsultando(true);
    const t = toast.loading('Consultando SEFAZ...');
    try {
      const { data } = await api.post(`/nfedist/${empresa.id}`);
      setNfedistStatus(prev => ({
        ...prev,
        ultima_consulta: new Date().toISOString(),
        ultimo_nsu:      data.ultimo_nsu,
        total_recebidos: (prev?.total_recebidos || 0) + (data.recebidos || 0)
      }));
      toast.success(data.mensagem, { id: t });
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao consultar SEFAZ.', { id: t });
    } finally {
      setConsultando(false);
    }
  }

  async function salvarCertificado() {
    if (!certArquivo) return toast.error('Selecione o arquivo .pfx.');
    if (!certForm.senha) return toast.error('Informe a senha do certificado.');
    setCertSalvando(true);
    try {
      const fd = new FormData();
      fd.append('pfx', certArquivo);
      fd.append('senha', certForm.senha);
      if (certForm.validade) fd.append('validade', certForm.validade);
      const { data } = await api.post(`/certificados/${empresa.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCertStatus(data.certificado);
      setCertModal(false);
      setCertForm({ senha: '', validade: '' });
      setCertArquivo(null);
      toast.success('Certificado salvo com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar certificado.');
    } finally {
      setCertSalvando(false);
    }
  }

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 size={28} className="animate-spin text-primary-500" />
    </div>
  );

  if (!empresa) return (
    <div className="card text-center py-12 text-slate-500">
      <Building2 size={32} className="mx-auto mb-2 opacity-30" />
      Nenhuma empresa vinculada à sua conta.
    </div>
  );

  const certVencido = certStatus?.validade && new Date(certStatus.validade) < new Date();

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Cabeçalho */}
      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-primary-400" />
        <div>
          <h1 className="text-base font-semibold text-slate-200">{empresa.razao_social}</h1>
          <p className="text-xs text-slate-500">{formatarCnpj(empresa.cnpj)}</p>
        </div>
      </div>

      {/* Certificado Digital A1 */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-primary-400" />
            <h2 className="text-sm font-semibold text-slate-300">Certificado Digital A1</h2>
          </div>
          <button onClick={() => setCertModal(true)} className="btn-primary text-xs py-1.5 px-3">
            <Upload size={13} /> {certStatus ? 'Substituir' : 'Enviar Certificado'}
          </button>
        </div>

        {certStatus ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              {certVencido
                ? <ShieldAlert size={18} className="text-red-400 flex-shrink-0" />
                : <ShieldCheck size={18} className="text-emerald-400 flex-shrink-0" />}
              <div>
                <p className="text-xs text-slate-600">Situação</p>
                <p className={certVencido ? 'text-red-400' : 'text-emerald-400'}>
                  {certVencido ? 'Vencido' : 'Válido'}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-600">Validade</p>
              <p className="text-slate-300">
                {certStatus.validade
                  ? new Date(certStatus.validade).toLocaleDateString('pt-BR')
                  : 'Não informada'}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-600">Arquivo</p>
              <p className="text-slate-400 text-xs font-mono">{certStatus.nome_arquivo || 'certificado.pfx'}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <ShieldAlert size={16} className="text-amber-500 flex-shrink-0" />
            <span>Nenhum certificado cadastrado. O certificado A1 é necessário para buscar NF-es na SEFAZ e registrar manifestações.</span>
          </div>
        )}
      </div>

      {/* NFeDistDFe */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CloudDownload size={16} className="text-sky-400" />
            <h2 className="text-sm font-semibold text-slate-300">Buscar NF-es na SEFAZ</h2>
          </div>
          <button
            onClick={consultarSEFAZ}
            disabled={consultando || !certStatus}
            className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50"
            title={!certStatus ? 'Cadastre o certificado A1 para habilitar' : ''}>
            {consultando
              ? <><Loader2 size={13} className="animate-spin" /> Consultando...</>
              : <><RefreshCw size={13} /> Consultar Agora</>}
          </button>
        </div>

        <p className="text-xs text-slate-500">
          Baixa automaticamente as NF-es de compras autorizadas pela SEFAZ. O sistema já faz isso
          automaticamente às 6h e 18h — use este botão para forçar uma consulta agora.
        </p>

        {!certStatus && (
          <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-900/20 border border-amber-800/30 rounded-lg p-3">
            <AlertCircle size={13} className="flex-shrink-0" />
            Cadastre o certificado A1 acima para habilitar a busca na SEFAZ.
          </div>
        )}

        {nfedistStatus?.ultima_consulta ? (
          <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t border-surface-border">
            <div>
              <p className="text-xs text-slate-600">Última consulta</p>
              <p className="text-slate-300">
                {new Date(nfedistStatus.ultima_consulta).toLocaleString('pt-BR')}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-600">NSU atual</p>
              <p className="text-slate-300 font-mono text-xs">
                {String(nfedistStatus.ultimo_nsu || 0).padStart(15, '0')}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-600">Total baixado</p>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={14} className="text-emerald-400" />
                <p className="text-emerald-400 font-semibold">{nfedistStatus.total_recebidos} NF-e(s)</p>
              </div>
            </div>
            {nfedistStatus.ultimo_erro && (
              <div className="col-span-3 text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg p-2">
                Último erro: {nfedistStatus.ultimo_erro}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-600">
            {certStatus
              ? 'Nenhuma consulta realizada ainda. Clique em "Consultar Agora" ou aguarde o próximo ciclo automático (6h e 18h).'
              : 'Aguardando cadastro do certificado.'}
          </p>
        )}
      </div>

      {/* Modal Certificado */}
      {certModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-surface-card border border-surface-border rounded-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-sm font-semibold text-slate-200">Enviar Certificado Digital A1</h3>

            <div
              onClick={() => certInputRef.current?.click()}
              className="border-2 border-dashed border-surface-border rounded-xl p-6 text-center cursor-pointer hover:border-primary-600/50 transition-colors">
              <Upload size={24} className="mx-auto mb-2 text-slate-600" />
              <p className="text-sm text-slate-500">
                {certArquivo ? certArquivo.name : 'Clique para selecionar o arquivo .pfx'}
              </p>
              <input ref={certInputRef} type="file" accept=".pfx" className="hidden"
                onChange={e => setCertArquivo(e.target.files[0])} />
            </div>

            <div>
              <label className="label">Senha do Certificado *</label>
              <input className="input font-mono" type="password" placeholder="Senha do arquivo .pfx"
                value={certForm.senha} onChange={e => setCertForm(f => ({...f, senha: e.target.value}))} />
            </div>

            <div>
              <label className="label">Data de Validade</label>
              <input className="input" type="date" value={certForm.validade}
                onChange={e => setCertForm(f => ({...f, validade: e.target.value}))} />
              <p className="text-xs text-slate-600 mt-1">Opcional — para controle de vencimento</p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 text-xs text-amber-400">
              O certificado e a senha são criptografados antes de armazenar. A senha nunca fica em texto puro.
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-surface-border">
              <button onClick={() => setCertModal(false)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
              <button onClick={salvarCertificado} disabled={certSalvando} className="btn-primary text-sm py-2 px-4">
                {certSalvando ? <><Loader2 size={14} className="animate-spin" /> Enviando...</> : 'Salvar Certificado'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
